<template>
  <v-app>
    <v-app-bar
      app
      color="white">
      <div class="d-flex align-center">
        <v-img
          alt="TUD Logo"
          class="shrink mr-2"
          contain
          :src="require('@/assets/logo.png')"
          transition="scale-transition"
          width="40"
        />
      </div>

      <v-toolbar-title>
        Laura's mapping app
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon color="error" @click="triggerResize" title="Redeseneaza harta">
        <v-icon>mdi-move-resize</v-icon>
      </v-btn>
      <v-btn icon color="error" @click="clearData" title="Sterge datele salvate local">
        <v-icon>mdi-nuke</v-icon>
      </v-btn>
      <v-btn icon color="primary" @click="saveData" title="Salveaza datele local">
        <v-icon>mdi-content-save</v-icon>
      </v-btn>
      <v-btn icon color="primary" @click="screenshot" title="Screenshot">
        <v-icon>mdi-camera</v-icon>
      </v-btn>

      <v-spacer></v-spacer>
      <v-slider
        v-model="zoom"
        class="align-center"
        :max="20"
        :min="1"
        step="0.1"
        thumb-label
        hide-details
        label="Zoom">
        <template v-slot:prepend>
          <v-icon
            @click="zoom -= 0.1">
            mdi-minus
          </v-icon>
        </template>
        <template v-slot:append>
          <v-icon
            @click="zoom += 0.1">
            mdi-plus
          </v-icon>
        </template>
      </v-slider>

      <v-spacer></v-spacer>
      <v-switch
        v-model="showBorders"
        label="Poligoane"
        hide-details
      ></v-switch>
      <v-switch
        v-model="showLabels"
        label="Etichete"
        hide-details
      ></v-switch>
      <v-switch
        v-model="draggablePolys"
        label="Poligoane draggable"
        hide-details
      ></v-switch>
      <v-switch
        v-model="editablePolys"
        label="Poligoane editabile"
        hide-details
      ></v-switch>
      <v-spacer></v-spacer>

      <v-btn icon color="#0fb693" @click="openSettings">
        <v-icon>mdi-cogs</v-icon>
      </v-btn>
      <v-btn icon color="#0fb693" @click="openXlsx">
        <v-icon>mdi-file-excel</v-icon>
      </v-btn>
      <v-btn icon color="#0fb693" @click="openMarkers">
        <v-icon>mdi-map-marker-multiple</v-icon>
      </v-btn>
    </v-app-bar>

    <v-main>
      <laura-map
        :settings="settings"
        :markers="markers"
        :polys="polys"
        :showBorders="showBorders"
        :showLabels="showLabels"
        :icons="icons"
        :iconOpacity="iconOpacity"
        :iconScale="iconScale"
        :iconStrokeWeight="iconStrokeWeight"
        :fontColor="fontColor"
        :fontSize="fontSize + 'px'"
        :fontWeight="String(fontWeight)"
        :labelX="labelX"
        :labelY="labelY"
        :zoom="zoom"
        :draggablePolys="draggablePolys"
        :editablePolys="editablePolys"
        :resize="resize"
        @polypathchanged="updatePolyPath"
        ref="mapComponent" />

      <v-dialog persistent scrollable v-model="settingsDialog" width="800">
        <v-card class="pa-md-4 mx-auto" max-height="75vh">
          <v-card-title>
            <h1 class="text-h6 text-center mb-3">Optiuni harta & poligoane</h1>
          </v-card-title>
          <v-card-text>
            <v-tabs
              v-model="settingsTab"
              background-color="transparent"
              grow>
              <v-tab>
                Harta
              </v-tab>
              <v-tab>
                Poligoane
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="settingsTab">
              <v-tab-item>
                  <a href="https://snazzymaps.com/" target="_blank">Customizeaza stiluri aici si apoi copy-paste codul mai jos.</a>

                  <v-alert type="error" v-if="this.error" dismissible @input="alertChanged">
                    {{ error }}
                  </v-alert>

                  <v-slide-group
                    v-model="selectedStyle"
                    class="pa-4"
                    show-arrows>
                    <v-slide-item v-slot:default="{ active, toggle }" v-for="item in styleOptions" :key="item">
                      <v-hover v-slot:default="{ hover }">
                        <v-card
                          :color="active ? 'primary lighten-2' : 'grey lighten-4'"
                          class="ma-4"
                          :elevation="hover ? 4 : 2"
                          height="100"
                          width="100"
                          @click="toggle">
                          <v-img
                            :class="{ 'card-image-hover': hover || active }"
                            class="primary--text align-end card-image"
                            height="200px"
                            :src="`/maps/${item}.png`">
                          </v-img>
                        </v-card>
                      </v-hover>
                    </v-slide-item>
                  </v-slide-group>

                  <v-textarea
                    class="my-2"
                    v-model="tempSettings"
                    label="Design harta"
                    hint="alege un exemplu din galerie sau foloseste linkul de mai sus pentru a genera codul"
                    persistent-hint>
                  </v-textarea>
              </v-tab-item>
              <v-tab-item>
                <v-card class="elevation-0">
                  <v-card-actions>
                    <v-btn icon @click="focusedPoly--" :disabled="focusedPoly === 0">
                      <v-icon>mdi-chevron-left-circle</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn @click="addPoly" icon color="primary">
                      <v-icon>mdi-plus</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn icon @click="focusedPoly++" :disabled="focusedPoly === polys.length - 1">
                      <v-icon>mdi-chevron-right-circle</v-icon>
                    </v-btn>
                  </v-card-actions>
                  <v-card-text>
                    <v-window v-model="focusedPoly">
                      <v-window-item v-for="(poly, index) in polys" :key="poly.id" :value="index">
                        <v-card
                          class="ma-4">
                          <v-card-text>
                            <v-text-field
                              v-model="poly.label"
                              label="Eticheta">
                            </v-text-field>

                            <v-textarea
                              class="my-2"
                              v-model="poly.path"
                              label="Poligon granite sector"
                              hint="un sir ciudat de caractere pe care il decodam"
                              persistent-hint>
                            </v-textarea>

                            <p style="color: rgba(0, 0, 0, 0.6)" class="mt-2">Culoare umplere poligon</p>
                            <v-color-picker
                              v-model="poly.options.fillColor"
                              show-swatches
                              canvas-height="50"
                              swatches-max-height="40px">
                            </v-color-picker>

                            <v-slider
                              v-model="poly.options.fillOpacity"
                              class="align-center mt-4"
                              :max="1"
                              :min="0"
                              step="0.01"
                              thumb-label
                              hide-details
                              label="Opacitate culoare umplere poligon">
                              <template v-slot:prepend>
                                <v-icon
                                  @click="poly.options.fillOpacity -= 0.01">
                                  mdi-minus
                                </v-icon>
                              </template>
                              <template v-slot:append>
                                <v-icon
                                  @click="poly.options.fillOpacity += 0.01">
                                  mdi-plus
                                </v-icon>
                              </template>
                            </v-slider>

                            <p style="color: rgba(0, 0, 0, 0.6)" class="mt-2">Culoare margine poligon</p>
                            <v-color-picker
                              v-model="poly.options.strokeColor"
                              show-swatches
                              canvas-height="50"
                              swatches-max-height="40px">
                            </v-color-picker>

                            <v-slider
                              v-model="poly.options.strokeOpacity"
                              class="align-center mt-4"
                              :max="1"
                              :min="0"
                              step="0.01"
                              thumb-label
                              hide-details
                              label="Opacitate culoare margine poligon">
                              <template v-slot:prepend>
                                <v-icon
                                  @click="poly.options.strokeOpacity -= 0.01">
                                  mdi-minus
                                </v-icon>
                              </template>
                              <template v-slot:append>
                                <v-icon
                                  @click="poly.options.strokeOpacity += 0.01">
                                  mdi-plus
                                </v-icon>
                              </template>
                            </v-slider>

                            <!-- new options for poly label -->
                            <v-textarea v-model="poly.options.markerPath" label="Cod iconita (1 svg path)">
                            </v-textarea>
                            <v-avatar>
                              <img :src="getSvgPolyImg(poly)" />
                            </v-avatar>

                            <p style="color: rgba(0, 0, 0, 0.6)" class="mt-2">Culoare umplere imagine eticheta</p>
                            <v-color-picker
                              v-model="poly.options.labelFillColor"
                              show-swatches
                              canvas-height="50"
                              swatches-max-height="40px">
                            </v-color-picker>

                            <v-slider
                              v-model="poly.options.labelFillOpacity"
                              class="align-center mt-4"
                              :max="1"
                              :min="0"
                              step="0.01"
                              thumb-label
                              hide-details
                              label="Opacitate culoare umplere imagine eticheta">
                              <template v-slot:prepend>
                                <v-icon
                                  @click="poly.options.labelFillOpacity -= 0.01">
                                  mdi-minus
                                </v-icon>
                              </template>
                              <template v-slot:append>
                                <v-icon
                                  @click="poly.options.labelFillOpacity += 0.01">
                                  mdi-plus
                                </v-icon>
                              </template>
                            </v-slider>

                            <p style="color: rgba(0, 0, 0, 0.6)" class="mt-2">Culoare margine imagine eticheta</p>
                            <v-color-picker
                              v-model="poly.options.labelStrokeColor"
                              show-swatches
                              canvas-height="50"
                              swatches-max-height="40px">
                            </v-color-picker>

                            <v-slider
                              v-model="poly.options.labelStrokeWeight"
                              class="align-center mt-4"
                              :max="20"
                              :min="0"
                              step="0.1"
                              thumb-label
                              hide-details
                              label="Grosime culoare margine imagine eticheta">
                              <template v-slot:prepend>
                                <v-icon
                                  @click="poly.options.labelStrokeWeight -= 0.1">
                                  mdi-minus
                                </v-icon>
                              </template>
                              <template v-slot:append>
                                <v-icon
                                  @click="poly.options.labelStrokeWeight += 0.1">
                                  mdi-plus
                                </v-icon>
                              </template>
                            </v-slider>
                          </v-card-text>

                          <p style="color: rgba(0, 0, 0, 0.6)" class="mt-2">Culoare font eticheta</p>
                            <v-color-picker
                              v-model="poly.options.fontColor"
                              show-swatches
                              canvas-height="50"
                              swatches-max-height="40px">
                            </v-color-picker>

                          <v-slider
                            v-model="poly.options.fontSize"
                            class="align-center mt-4"
                            :max="100"
                            :min="0"
                            step="0.01"
                            thumb-label
                            hide-details
                            label="Dimensiune font eticheta">
                            <template v-slot:prepend>
                              <v-icon
                                @click="poly.options.fontSize -= 1">
                                mdi-minus
                              </v-icon>
                            </template>
                            <template v-slot:append>
                              <v-icon
                                @click="poly.options.fontSize += 1">
                                mdi-plus
                              </v-icon>
                            </template>
                          </v-slider>

                          <v-slider
                            v-model="poly.options.fontWeight"
                            class="align-center mt-4"
                            :max="900"
                            :min="0"
                            step="100"
                            thumb-label
                            hide-details
                            label="Grosime font eticheta (0-900)">
                            <template v-slot:prepend>
                              <v-icon
                                @click="poly.options.fontWeight -= 100">
                                mdi-minus
                              </v-icon>
                            </template>
                            <template v-slot:append>
                              <v-icon
                                @click="poly.options.fontWeight += 100">
                                mdi-plus
                              </v-icon>
                            </template>
                          </v-slider>

                          <v-slider
                            v-model="poly.options.labelScale"
                            class="align-center mt-4"
                            :max="10"
                            :min="0"
                            step="0.001"
                            thumb-label
                            hide-details
                            label="Dimensiune imagine eticheta">
                            <template v-slot:prepend>
                              <v-icon
                                @click="poly.options.labelScale -= 0.001">
                                mdi-minus
                              </v-icon>
                            </template>
                            <template v-slot:append>
                              <v-icon
                                @click="poly.options.labelScale += 0.001">
                                mdi-plus
                              </v-icon>
                            </template>
                          </v-slider>
                          <v-card-actions>
                            <v-btn icon @click="deletePoly(index)" color="error">
                              <v-icon>mdi-delete</v-icon>
                            </v-btn>
                            <v-spacer></v-spacer>
                            <v-btn icon @click="poly.options.visible = !poly.options.visible" :color="poly.options.visible ? 'info' : 'warning'">
                              <v-icon>{{ poly.options.visible ? 'mdi-eye' : 'mdi-eye-off' }}</v-icon>
                            </v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-window-item>
                    </v-window>
                  </v-card-text>
                  <v-card-actions>
                    <v-btn icon @click="focusedPoly--" :disabled="focusedPoly === 0">
                      <v-icon>mdi-chevron-left-circle</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn @click="addPoly" icon color="primary">
                      <v-icon>mdi-plus</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn icon @click="focusedPoly++" :disabled="focusedPoly === polys.length - 1">
                      <v-icon>mdi-chevron-right-circle</v-icon>
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="closeSettings">
              Inchide
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog persistent scrollable v-model="xlsxDialog" width="500">
        <v-card class="pa-md-4 mx-auto" max-height="75vh">
          <v-card-title>
            <h1 class="text-h6 text-center mb-3">Incarcare markere</h1>
          </v-card-title>
          <v-card-text>
            <a href="/Adrese Piete.xlsx">
              Exemplu fisier
            </a>

            <v-file-input
              ref="xlsx"
              v-model="xlsx"
              label="Fisier XLSX"
              hint="fisierul cu adresele/denumirile"
              persistent-hint
              class="my-2">
            </v-file-input>

            <v-text-field
              v-model.number="sheet"
              label="Sheet"
              hint="sheetul cu informatii"
              persistent-hint
              class="my-2"
              prepend-icon="mdi-google-spreadsheet">
            </v-text-field>
            <v-text-field
              v-model.number="columnCuloare"
              type="number"
              min="0"
              step="1"
              label="Coloana culoare"
              hint="coloana pentru culoare obiectiv (se incepe de la 0)"
              persistent-hint
              class="my-2"
              prepend-icon="mdi-table-column">
            </v-text-field>
            <v-text-field
              v-model.number="columnTip"
              type="number"
              min="0"
              step="1"
              label="Coloana tip"
              hint="coloana pentru tip obiectiv (se incepe de la 0)"
              persistent-hint
              class="my-2"
              prepend-icon="mdi-table-column">
            </v-text-field>
            <v-text-field
              v-model.number="columnDenumire"
              type="number"
              min="0"
              step="1"
              label="Coloana denumire"
              hint="coloana pentru denumire obiectiv (se incepe de la 0)"
              persistent-hint
              class="my-2"
              prepend-icon="mdi-table-column">
            </v-text-field>
            <v-text-field
              v-model.number="columnAdresa"
              type="number"
              min="0"
              step="1"
              label="Coloana adresa"
              hint="coloana pentru adresa obiectiv (se incepe de la 0)"
              persistent-hint
              class="my-2"
              prepend-icon="mdi-table-column">
            </v-text-field>
            <v-text-field
              v-model.number="rowStart"
              type="number"
              min="0"
              step="1"
              label="Rand inceput"
              hint="randul de pe care incep datele (se incepe de la 0, nu se include randul cu capul de tabel)"
              persistent-hint
              class="my-2"
              prepend-icon="mdi-table-row">
            </v-text-field>

            <v-switch
              v-model="append"
              :label="append ? 'Adaugare markere' : 'Inlocuire markere'">
            </v-switch>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="closeXlsx">
              Anulare
            </v-btn>
            <v-btn
              color="primary"
              @click="parseXlsx"
              :disabled="xlsx === null || columnAdresa === null || columnDenumire === null || rowStart === null">
              Citeste informatiile
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog persistent scrollable v-model="markersDialog" width="500">
        <v-card class="pa-md-4 mx-auto" max-height="75vh">
          <v-card-title>
            <h1 class="text-h6 text-center mb-3">Gestiune markere</h1>
          </v-card-title>
          <v-card-text>
            <v-tabs
              v-model="tab"
              background-color="transparent"
              grow>
              <v-tab>
                Markere
              </v-tab>
              <v-tab>
                Iconite
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="tab">
              <v-tab-item>
                <v-list dense three-line>
                  <v-list-item
                    v-for="(item, index) in markers"
                    :key="item.id">
                    <v-list-item-avatar size="48">
                      <img :src="getSvgImg(icons[`${item.tip} - ${item.culoare}`])" />
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ index }}.
                        {{ item.denumire }}
                        |
                        {{ item.tip }}
                        |
                        {{ item.culoare }}
                      </v-list-item-title>
                      <v-list-item-subtitle>{{ item.adresa }}</v-list-item-subtitle>
                      <v-list-item-subtitle>{{ item.location }}</v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn icon color="red" @click="deleteMarker(index)">
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-tab-item>
              <v-tab-item>
                <v-list dense>
                  <v-list-item
                    v-for="pair in icons"
                    :key="pair.label"
                    @click="selectedIconIndex = pair.label">
                    <v-list-item-avatar size="48">
                      <img :src="getSvgImg(pair)" />
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ pair.label }}
                      </v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>

                <v-card>
                  <v-card-title>
                    <div class="text-subtitle-1">Optiuni generale</div>
                  </v-card-title>
                  <v-card-text>
                    <v-slider
                      v-model="iconOpacity"
                      class="align-center"
                      :max="1"
                      :min="0"
                      step="0.01"
                      thumb-label
                      hide-details
                      label="Opacitate culoare marker">
                      <template v-slot:prepend>
                        <v-icon
                          @click="iconOpacity -= 0.01">
                          mdi-minus
                        </v-icon>
                      </template>
                      <template v-slot:append>
                        <v-icon
                          @click="iconOpacity += 0.01">
                          mdi-plus
                        </v-icon>
                      </template>
                    </v-slider>
                    <v-slider
                      v-model="iconScale"
                      class="align-center"
                      :max="20"
                      :min="0.1"
                      step="0.1"
                      thumb-label
                      hide-details
                      label="Dimensiune marker">
                      <template v-slot:prepend>
                        <v-icon
                          @click="iconScale -= 0.1">
                          mdi-minus
                        </v-icon>
                      </template>
                      <template v-slot:append>
                        <v-icon
                          @click="iconScale += 0.1">
                          mdi-plus
                        </v-icon>
                      </template>
                    </v-slider>
                    <v-slider
                      v-model="iconStrokeWeight"
                      class="align-center"
                      :max="10"
                      :min="0"
                      step="0.1"
                      thumb-label
                      hide-details
                      label="Grosime contur marker">
                      <template v-slot:prepend>
                        <v-icon
                          @click="iconStrokeWeight -= 0.1">
                          mdi-minus
                        </v-icon>
                      </template>
                      <template v-slot:append>
                        <v-icon
                          @click="iconStrokeWeight += 0.1">
                          mdi-plus
                        </v-icon>
                      </template>
                    </v-slider>
                    <v-slider
                      v-model="fontSize"
                      class="align-center"
                      :max="200"
                      :min="2"
                      step="1"
                      thumb-label
                      hide-details
                      label="Marime font eticheta">
                      <template v-slot:prepend>
                        <v-icon
                          @click="fontSize -= 1">
                          mdi-minus
                        </v-icon>
                      </template>
                      <template v-slot:append>
                        <v-icon
                          @click="fontSize += 1">
                          mdi-plus
                        </v-icon>
                      </template>
                    </v-slider>

                    <v-slider
                      v-model="fontWeight"
                      class="align-center"
                      :max="900"
                      :min="100"
                      step="100"
                      thumb-label
                      hide-details
                      label="Greutate font eticheta">
                      <template v-slot:prepend>
                        <v-icon
                          @click="fontWeight -= 100">
                          mdi-minus
                        </v-icon>
                      </template>
                      <template v-slot:append>
                        <v-icon
                          @click="fontWeight += 100">
                          mdi-plus
                        </v-icon>
                      </template>
                    </v-slider>

                    <v-slider
                      v-model="labelX"
                      class="align-center"
                      :max="200"
                      :min="-200"
                      step="1"
                      thumb-label
                      hide-details
                      label="Asezare eticheta (X)">
                      <template v-slot:prepend>
                        <v-icon
                          @click="labelX -= 1">
                          mdi-minus
                        </v-icon>
                      </template>
                      <template v-slot:append>
                        <v-icon
                          @click="labelX += 1">
                          mdi-plus
                        </v-icon>
                      </template>
                    </v-slider>
                    <v-slider
                      v-model="labelY"
                      class="align-center"
                      :max="200"
                      :min="-200"
                      step="1"
                      thumb-label
                      hide-details
                      label="Asezare eticheta (Y)">
                      <template v-slot:prepend>
                        <v-icon
                          @click="labelY -= 1">
                          mdi-minus
                        </v-icon>
                      </template>
                      <template v-slot:append>
                        <v-icon
                          @click="labelY += 1">
                          mdi-plus
                        </v-icon>
                      </template>
                    </v-slider>

                    <p style="color: rgba(0, 0, 0, 0.6)" class="mt-2">Culoare eticheta</p>
                    <v-color-picker
                      v-model="fontColor"
                      show-swatches
                      canvas-height="100"
                      swatches-max-height="100px">
                    </v-color-picker>
                  </v-card-text>
                </v-card>

                <v-card v-if="selectedIconIndex" class="mt-2">
                  <v-card-title>
                    <div class="text-subtitle-1">Optiuni marker {{ selectedIconIndex }}</div>
                    <v-spacer></v-spacer>
                    <v-icon color="error" @click="selectedIconIndex = null">mdi-close</v-icon>
                  </v-card-title>
                  <v-card-text>
                    <v-textarea v-model="icons[selectedIconIndex].path" label="Cod iconita">
                    </v-textarea>

                    <p style="color: rgba(0, 0, 0, 0.6)" class="mt-2">Culoare iconita</p>
                    <v-color-picker
                      v-model="icons[selectedIconIndex].color"
                      show-swatches
                      canvas-height="100"
                      swatches-max-height="100px">
                    </v-color-picker>

                    <p class="text-subtitle-1">Exemple iconite</p>
                    <v-card v-for="(path, key) in markersPathsDefault" :key="key" class="elevation-0" mt-2>
                      <v-card-title>
                        <div class="text-subtitle-2">{{ key }}</div>
                      </v-card-title>
                      <v-card-text>
                        <v-avatar>
                          <img :src="exampleSvgImg(path)">
                        </v-avatar>
                        <br>
                        {{ path }}
                      </v-card-text>
                    </v-card>
                  </v-card-text>
                </v-card>
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              text
              @click="closeMarkers">
              Inchide
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-main>
  </v-app>
</template>

<script>
import MapView from '@/components/Map.vue'
import XLSX from 'xlsx'
import uniqid from 'uniqid'
import _debounce from 'lodash.debounce'

export default {
  name: 'App',

  components: {
    'laura-map': MapView
  },

  data: () => ({
    draggablePolys: false,
    editablePolys: false,
    focusedPoly: 0,
    settingsTab: null,
    tab: null,
    /* IMPORTANT: compiler adds slashes because reasons, so always add fucking slashes... */
    polys: [
      {
        id: uniqid(),
        label: 'Sectorul 6',
        path: 'qjfnGczb~CrBrDdEhNbCzHzCfJXhG~ClOnLHtB@hBi@pFsFjA}@fC?x@l@pCjBr@f@rN~@xCT`AoBxF^~@gAxAg@dBC@rAlAJT{@p@AA|A|AFHuAf@IFfEz@PtA^dDdAhCbM`BxHjHr]p@zNLtCJpFThNjAjPlBp]VzDvAhUp@rKv@zMz@bNx@`N|@zMr@`Mt@`Mx@pMhIrrAn@rKj@jJBZ|DrRdDtOFZTvAEpOOlT@pCPvB`CxW~@bGNdAy{@bb@gc@vJiDtBynBdHcNq~GkGkRgDoJaBmEm@cBQa@iA[}LwBoKlM}ChDcC~FyHvAsGcLmI~HaIvG_AzCMzLq@rEqFt\\cGn^sGt_@sJz_@wGhWsDpNs@hEZlFhBpK{Bl@kD_W}Cp@mCNoCu@sFfMq@GI_Cib@sg@cHqQmBgMyAg\\PuYzCgTlNop@lJu_@rEsRhJ~JxFsKhA}K~G}\\pIcb@nDcQvWoo@jFeMfGmOhFiNbFyS`DcArJgHhHoD~DaBhBoAjDmFlAuB|E{HlAiArCsA\\eAZiDxBgDdHmKjEiGnI{MlAuAxHgEvCmA\\Q|KjN',
        options: {
          visible: true,
          fillColor: '#0fb693',
          strokeColor: '#0fb693',
          fillOpacity: 0.6,
          strokeOpacity: 0.6,
          labelFillColor: '#ff0000',
          labelFillOpacity: 1,
          labelScale: 0.1,
          labelStrokeWeight: 1,
          labelStrokeColor: '#000000',
          fontSize: 14,
          fontWeight: 400,
          fontColor: '#000000',
          markerPath: 'M256,0C153.755,0,70.573,83.182,70.573,185.426c0,126.888,165.939,313.167,173.004,321.035    c6.636,7.391,18.222,7.378,24.846,0c7.065-7.868,173.004-194.147,173.004-321.035C441.425,83.182,358.244,0,256,0z M256,278.719    c-51.442,0-93.292-41.851-93.292-93.293S204.559,92.134,256,92.134s93.291,41.851,93.291,93.293S307.441,278.719,256,278.719z'
        },
        center: null
      },
      {
        id: uniqid(),
        label: 'Sectorul 1',
        path: 'y{lnGklq}Cib@sg@cHqQmBgMyAg\\PuYzCgTlNop@lJu_@rEsRhJ~JxFsKhA}K~G}\\pIcb@nDcQvWoo@jFeMfGmOhFiNbFyS`DcArJgHhHoD~DaBhBoAjDmFlAuB|E{HlAiArCsA\\eAZiDxAyB^m@dHmKjEiGnI{MlAuAxHgEtD_B|KjNbNm[vGmTOaq@AeF]{@AiA\\mAAqC]uf@OeF?[CeAImEMaBWuE?GOkBa@gEOsAUaA_@m@Ko@GIo@f@QJSJoB~@yCf@yChA_O~GUkCuFGoAU?uAKsAUcAM_@M]]m@S[mAeAqCuAQm@I]u@iAs@tAiBVgTvCoD`@yB|AuBlBy@j@cA{@kFlAwAzBcLNoDD_LV_P{BeAGwKqCgLsAoBU{AMcAMGn@w@\\YIgEiCyCaBmIoEmCkAmDwAqAJs@U_GzBe@XaK`GgKvE{DjBoAp@iAd@y@sEeBsHc@uAaHyN{JiSqGwMiFsIg@e@u@u@y@{A_RhZgI{Eo]pQvClGaBdBoDj@yN`GYoAgErA[{B{A}@iB[aDyW}NmmAj@uSqHwA}Bi@aIfCxC`TvAzK~CpV`Gxd@LdD~Fhe@wKpBmDlAaE|@eh@yHGwLeQkA[~HqBfAmD`CkMsBmNO}U~CqGrEss@}C{G_@DhLqJh@L`KXzKhBR@tAv@pBrGa@`Cn`@fBb\\lDze@~@jMqIzBmPlEWfHi@dOvCtFnGxLh@TNxBx@NlFZ|DVrLt@nFj\\~RtmAh@jDb@`DJ`AN`AVhAfA`EhAtDhDtKdFtPvRtl@{XvZb@tBhEpPjDvMvD`BvC|ApElBrJwHfC_@rC|AlB`EbOxd@rFtPtBfJlC|ElBjBfD`ClATnw@|JnQnBrBhTGnUnDtF~HpPlEpByCrGgOxb@~DxGpB`TrDd@\\}AhGbEpHmLpDdFnLgSdf@jOl`@~StOxB|]]I_C',
        options: {
          visible: true,
          fillColor: '#0fb693',
          strokeColor: '#0fb693',
          fillOpacity: 0.6,
          strokeOpacity: 0.6,
          labelFillColor: '#ff0000',
          labelFillOpacity: 1,
          labelScale: 0.1,
          labelStrokeWeight: 1,
          labelStrokeColor: '#000000',
          fontSize: 14,
          fontWeight: 400,
          fontColor: '#000000',
          markerPath: 'M256,0C153.755,0,70.573,83.182,70.573,185.426c0,126.888,165.939,313.167,173.004,321.035    c6.636,7.391,18.222,7.378,24.846,0c7.065-7.868,173.004-194.147,173.004-321.035C441.425,83.182,358.244,0,256,0z M256,278.719    c-51.442,0-93.292-41.851-93.292-93.293S204.559,92.134,256,92.134s93.291,41.851,93.291,93.293S307.441,278.719,256,278.719z'
        },
        center: null
      },
      {
        id: uniqid(),
        label: 'Sectorul 2',
        path: '{ninGoct~CaFjF}BbFsW{QidAgjA{HaIwOkPyQqXyDqRaq@piLgA_@x@zAt@t@f@d@hFrIpGvM|Sbc@b@tAdBrHx@rExCwAzDkBfKwE`KaGd@Y~F{Br@TpAKlDvAlCjAlInExC`BfEhCXHlBVpDVnOxAdLfA~OzB~KWnDEbLOz@UjGaB~@Uv@k@vBmBxB}AnDa@fTwChBWr@uAt@hAZjApCtAlAdARZ\\l@L\\L^TbAJrA?tAnATtFFTjCpO_G`HqCnB}@RMPKn@g@As@}@}Hg@}EIyArGaEpBuBNoA_B{EmBoGc@oDu@}KdAdArAp@p@}Bd@u@nAaBdAq@tAc@g@eHLoGz@s[hAMDg@iAQt@}DgCr@U_AnCuBtBcU_AKDc@`AB~BcZE}IkAeT{Ay@c@wEbCw@m@mNqAm^]iKoA{b@s@yFuPmfAaOgmAyJu_AkAoJoAoQo@mHkAkFy@yByBtAcIvDaDnAeElEoBhKNnExA`EhBdBbDdBbCBlCfCRvEMxGeDfHsBrDmAvFcAhHv@jHpAfD|AfD~BxE`@fEe@xGqCbDeDCeDCmCuAsEiEmCyDuEC}AwAqBuAyBqAwC\\oBl@wBpBSbBK|BHtBHrBt@tBf@jBzBdCbAhGTfDpAtCpAfDAdDYdDcCpBoCn@gApBYtDdAvDj@jHAvD{@^uBeBwCkEoBf@_@xAAtCpAtBrEhDXhIaCe@EF',
        options: {
          visible: true,
          fillColor: '#0fb693',
          strokeColor: '#0fb693',
          fillOpacity: 0.6,
          strokeOpacity: 0.6,
          labelFillColor: '#ff0000',
          labelFillOpacity: 1,
          labelScale: 0.1,
          labelStrokeWeight: 1,
          labelStrokeColor: '#000000',
          fontSize: 14,
          fontWeight: 400,
          fontColor: '#000000',
          markerPath: 'M256,0C153.755,0,70.573,83.182,70.573,185.426c0,126.888,165.939,313.167,173.004,321.035    c6.636,7.391,18.222,7.378,24.846,0c7.065-7.868,173.004-194.147,173.004-321.035C441.425,83.182,358.244,0,256,0z M256,278.719    c-51.442,0-93.292-41.851-93.292-93.293S204.559,92.134,256,92.134s93.291,41.851,93.291,93.293S307.441,278.719,256,278.719z'
        },
        center: null
      },
      {
        id: uniqid(),
        label: 'Sectorul 3',
        path: '{zenG_fi~C@r@FHJn@^l@T`ANrA`@fENjB?FVtEL`Bv@S|AV|EbCx@^\\PnBt@dCb@bG|@Vm@t@@Lw@XyH\\gGVaBp@gBTi@|HsEvA_A`CuKh@wM~@uFfBiDlJyCdTeG~CkAdMsN|`@}b@lHkJjDmFdEsMf[kmBlIog@vRsmATk@pXsbBdAaKN{JcGq}DMgKoSXObCo@nBY~@aCbBuHr@eCt@aBjCmBdKg@nDeJsBxA_NuCuCaCmPc@mCWuCA{CeA}MkJ`A{Fb@ke@`D}LgApD_TqGi@aLsB{FeBaCaAqEyBZ}BmGkB}ApK}i@qRcRqGmC`N_@pIVzKg@jK_BjKyAfJr@~K|A`M]hGmB|DqBrCcC|CcCtBmBxDUjGYxGAtDg@hGyBtFwFzFoCzBx@xBjAjFn@lHnAnQjAnJxJt_A`OfmAtPlfAr@xFnAzb@\\hKpAl^l@lNcCv@b@vEzAx@jAdTD|I_CbZaACEb@~@JuBbUoCtBT~@fCs@u@|DhAPEf@iAL{@r[MnGf@dHuAb@eAp@oA`Be@t@q@|BsAq@eAeAt@|Kb@nDlBnG~AzEOnAqBtBsG`EHxAf@|E|@|H',
        options: {
          visible: true,
          fillColor: '#0fb693',
          strokeColor: '#0fb693',
          fillOpacity: 0.6,
          strokeOpacity: 0.6,
          labelFillColor: '#ff0000',
          labelFillOpacity: 1,
          labelScale: 0.1,
          labelStrokeWeight: 1,
          labelStrokeColor: '#000000',
          fontSize: 14,
          fontWeight: 400,
          fontColor: '#000000',
          markerPath: 'M256,0C153.755,0,70.573,83.182,70.573,185.426c0,126.888,165.939,313.167,173.004,321.035    c6.636,7.391,18.222,7.378,24.846,0c7.065-7.868,173.004-194.147,173.004-321.035C441.425,83.182,358.244,0,256,0z M256,278.719    c-51.442,0-93.292-41.851-93.292-93.293S204.559,92.134,256,92.134s93.291,41.851,93.291,93.293S307.441,278.719,256,278.719z'
        },
        center: null
      },
      {
        id: uniqid(),
        label: 'Sectorul 4',
        path: '}r~mG_vt~CwRrmAmIng@g[jmBeErMkDlFmHjJ}`@|b@eMrN_DjAeTdGmJxCgBhD_AtFi@vMaCtKwA~@}HrEgApCW`B]fGYxHMv@u@AWl@qAlAnApB`@f@|@Bd@YpBRl@v@VdA`@f@\\`@t@h@xL|FfCtAxHfD~CjGM`GCfAbC@t@XhDe@vEMrAAtD?~@iCjAoEb@wArEpA`AgAj@o@v@nAl@@`FfDzE_GfBdAn@m@i@wEnDqCzCnIbLyIdCwDpC_Ft@s@xAhB\\gBvG?lCsAvAaAdCgAjExLlCsChD|F`F~GbBeBoEgJjBcN~HzDlLhJbObEbRpFzBpAlBXnEfBlO[`CYnEtBzCjEfHbIgEnDxJGaDkWWqAtF~@zBgDbQxCv^tG_AnN|WwHJsA|QgLvd@sVbsAgx@uf@sm@db@_~@o@{Ct_AunBoCg[}@wGgAmNa@mFk@aGk@gGeT{cAm~A`cAwoA~}@kIsFmBlGuZoSya@{XpAwEc^uVo@bCcI`@mJsKes@ad@Uj@',
        options: {
          visible: true,
          fillColor: '#0fb693',
          strokeColor: '#0fb693',
          fillOpacity: 0.6,
          strokeOpacity: 0.6,
          labelFillColor: '#ff0000',
          labelFillOpacity: 1,
          labelScale: 0.1,
          labelStrokeWeight: 1,
          labelStrokeColor: '#000000',
          fontSize: 14,
          fontWeight: 400,
          fontColor: '#000000',
          markerPath: 'M256,0C153.755,0,70.573,83.182,70.573,185.426c0,126.888,165.939,313.167,173.004,321.035    c6.636,7.391,18.222,7.378,24.846,0c7.065-7.868,173.004-194.147,173.004-321.035C441.425,83.182,358.244,0,256,0z M256,278.719    c-51.442,0-93.292-41.851-93.292-93.293S204.559,92.134,256,92.134s93.291,41.851,93.291,93.293S307.441,278.719,256,278.719z'
        },
        center: null
      },
      {
        id: uniqid(),
        label: 'Sectorul 5',
        path: 'ctxmG}mf~CqN}Bcf@oIwKcBoMyBs]r@cPNgAQiBi@sBu@wQ{G{ReH{NiFaGaBeG[yFN{F~CkAb@yFbAoD`AwAHWQ_AdBqC~EeCvDcLxI{CoI_D~ByIpMaFgDm@Aw@oAk@n@aAfAsEqAc@vAkAnE_AhCuD?sA@wELiDd@u@YcCABgALaG_DkGyHgDgCuAyL}Fu@i@]a@a@g@WeAm@w@qBSe@X}@Ca@g@oAqBpAmAcG}@eCc@oBu@]Qy@_@}EcC}AWw@RHlEBdA?ZNdF\\tf@@pC]lA@hA\\z@@dFN`q@wGlTsMnZO\\\\h@T^PQl@zBdEhNbCzHzCfJXhG~ClOnLHtB@hBi@pFsFjA}@fC?x@l@pCjBr@f@rN~@xCT`AoBxF^~@gA~BzElGh@vKt@hCbM`BxHjHr]p@zNLtCJpFThNjAjPlBp]VzDvAhUp@rKv@zMz@bNx@`N|@zMr@`Mt@`Mx@pMhIrrAn@rKj@jJrFaA`Di@lOiCdEs@hGqAkBqKaCqWvHyAgK{U{J_UrMwMYm@hP}Q|f@oh@|K_MDZ~GaIVc@?m@j@kBbDoBs@qB~@sBjDgFhByAqJmUaA`A_AgBt@y@hFkGxL{O`Wve@pRw[hKuR{BsGtIy\\oAKnHqi@mVuJ~GwVlEcOdEuPdGgYjFeE^i@vAyFlAyEfAmEjBqHjKya@xCue@j@gGzCuA',
        options: {
          visible: true,
          fillColor: '#0fb693',
          strokeColor: '#0fb693',
          fillOpacity: 0.6,
          strokeOpacity: 0.6,
          labelFillColor: '#ff0000',
          labelFillOpacity: 1,
          labelScale: 0.1,
          labelStrokeWeight: 1,
          labelStrokeColor: '#000000',
          fontSize: 14,
          fontWeight: 400,
          fontColor: '#000000',
          markerPath: 'M256,0C153.755,0,70.573,83.182,70.573,185.426c0,126.888,165.939,313.167,173.004,321.035    c6.636,7.391,18.222,7.378,24.846,0c7.065-7.868,173.004-194.147,173.004-321.035C441.425,83.182,358.244,0,256,0z M256,278.719    c-51.442,0-93.292-41.851-93.292-93.293S204.559,92.134,256,92.134s93.291,41.851,93.291,93.293S307.441,278.719,256,278.719z'
        },
        center: null
      }
    ],
    selectedPoly: null,
    showBorders: true,
    showLabels: true,

    settingsDialog: false,
    settings: {},
    tempSettings: null,
    error: null,

    markers: [],

    xlsxDialog: false,
    xlsx: null,
    append: false,
    columnDenumire: 2,
    columnAdresa: 3,
    columnCuloare: 0,
    columnTip: 1,
    rowStart: 3,
    sheet: ' Centralizator locatii',
    readObjects: [],

    markersDialog: false,

    markersPathsDefault: {
      piata1: 'm 7.968696,11.168948 c -0.35743,0 -0.66904,-0.20621 -0.82027,-0.50407 v 0 l -1.19603,-4.3350401 -0.04582,-0.20163 a 0.45825,0.45825 0 0 1 0.45825,-0.45825 h 2.09878 l 2.10795,-3.0107 c 0.08248,-0.11914 0.21996,-0.19705 0.37576,-0.19705 0.1558,0 0.29786,0.0779 0.38035,0.20163 l 2.10336,3.00611 h 2.09878 a 0.45825,0.45825 0 0 1 0.45825,0.45825 l -0.01833,0.13289 -1.22353,4.4037701 c -0.15122,0.29786 -0.46283,0.50407 -0.82027,0.50407 h -5.95724 m 2.97862,-7.4511301 -1.37475,1.95214 h 2.7495 l -1.37475,-1.95214 m 0,3.78514 a 0.9165,0.9165 0 0 0 -0.9165,0.9165 0.9165,0.9165 0 0 0 0.9165,0.9165 0.9165,0.9165 0 0 0 0.9165,-0.9165 0.9165,0.9165 0 0 0 -0.9165,-0.9165 z m 0.05835,-7.28215019 A 7,7 0 0 0 4.0056565,7.2207877 c 0,5.2500003 6.9999995,13.0000003 6.9999995,13.0000003 0,0 7,-7.75 7,-13.0000003 a 7,7 0 0 0 -7,-6.99999999 z',
      piata2: 'M 12.074219 1.4921875 A 7 7 0 0 0 5.0742188 8.4921875 C 5.0742188 13.742188 12.074219 21.492188 12.074219 21.492188 C 12.074219 21.492188 19.074219 13.742188 19.074219 8.4921875 A 7 7 0 0 0 12.074219 1.4921875 z M 7.296875 4.3652344 L 8.8730469 4.3652344 L 9.3261719 5.328125 L 16.455078 5.328125 A 0.482 0.482 0 0 1 16.9375 5.8105469 C 16.9375 5.8924869 16.914599 5.9736613 16.880859 6.0507812 L 15.154297 9.1699219 C 14.990417 9.4639419 14.672047 9.6660156 14.310547 9.6660156 L 10.720703 9.6660156 L 10.287109 10.451172 C 10.277449 10.465632 10.271484 10.485666 10.271484 10.509766 A 0.1205 0.1205 0 0 0 10.392578 10.630859 L 15.974609 10.630859 L 15.974609 11.59375 L 10.189453 11.59375 A 0.96401 0.96401 0 0 1 9.2265625 10.630859 C 9.2265625 10.462159 9.2694969 10.302929 9.3417969 10.167969 L 9.9960938 8.9863281 L 8.2617188 5.328125 L 7.296875 5.328125 L 7.296875 4.3652344 z M 10.189453 12.076172 C 10.719663 12.076172 11.154297 12.505996 11.154297 13.041016 A 0.96401 0.96401 0 0 1 10.189453 14.003906 A 0.96401 0.96401 0 0 1 9.2265625 13.041016 C 9.2265625 12.505986 9.654433 12.076172 10.189453 12.076172 z M 15.009766 12.076172 C 15.539976 12.076172 15.974609 12.505996 15.974609 13.041016 A 0.96401 0.96401 0 0 1 15.009766 14.003906 A 0.96401 0.96401 0 0 1 14.044922 13.041016 C 14.044922 12.505986 14.474746 12.076172 15.009766 12.076172 z ',
      parcare1: 'M 12.074219 1.5332031 A 7 7 0 0 0 5.0742188 8.5332031 C 5.0742188 13.783203 12.074219 21.533203 12.074219 21.533203 C 12.074219 21.533203 19.074219 13.783203 19.074219 8.5332031 A 7 7 0 0 0 12.074219 1.5332031 z M 8.7285156 3.2070312 L 13.046875 3.2070312 C 15.088715 3.2070312 16.748047 4.8601831 16.748047 6.9082031 A 3.70123 3.70123 0 0 1 13.046875 10.609375 L 11.195312 10.609375 L 11.195312 14.310547 L 8.7285156 14.310547 L 8.7285156 3.2070312 z M 11.195312 5.6738281 L 11.195312 8.140625 L 13.169922 8.140625 A 1.23374 1.23374 0 0 0 14.404297 6.9082031 A 1.23374 1.23374 0 0 0 13.169922 5.6738281 L 11.195312 5.6738281 z ',
      parcare2: 'M 12 2 A 7 7 0 0 0 5 9 C 5 14.25 12 22 12 22 C 12 22 19 14.25 19 9 A 7 7 0 0 0 12 2 z M 8.9238281 4.7011719 L 15.056641 4.7011719 C 15.424591 4.7011719 15.736156 4.9344625 15.847656 5.2578125 L 17.007812 8.6035156 L 17.007812 13.0625 A 0.55749 0.55749 0 0 1 16.451172 13.621094 L 15.892578 13.621094 A 0.55749 0.55749 0 0 1 15.335938 13.0625 L 15.335938 12.505859 L 8.6464844 12.505859 L 8.6464844 13.0625 A 0.55749 0.55749 0 0 1 8.0878906 13.621094 L 7.53125 13.621094 A 0.55749 0.55749 0 0 1 6.9726562 13.0625 L 6.9726562 8.6035156 L 8.1328125 5.2578125 C 8.2443125 4.9344725 8.5558781 4.7011719 8.9238281 4.7011719 z M 8.9238281 5.5371094 L 8.0878906 8.0449219 L 15.892578 8.0449219 L 15.056641 5.5371094 L 8.9238281 5.5371094 z M 8.9179688 9.1601562 A 0.83624 0.83624 0 0 0 8.0878906 9.9960938 A 0.83624 0.83624 0 0 0 8.9238281 10.832031 A 0.83624 0.83624 0 0 0 9.7597656 9.9960938 A 0.83624 0.83624 0 0 0 8.9238281 9.1601562 A 0.83624 0.83624 0 0 0 8.9179688 9.1601562 z M 15.050781 9.1601562 A 0.83624 0.83624 0 0 0 14.220703 9.9960938 A 0.83624 0.83624 0 0 0 15.056641 10.832031 A 0.83624 0.83624 0 0 0 15.892578 9.9960938 A 0.83624 0.83624 0 0 0 15.056641 9.1601562 A 0.83624 0.83624 0 0 0 15.050781 9.1601562 z ',
      parcare3: 'M 12 2 A 7 7 0 0 0 5 9 C 5 14.25 12 22 12 22 C 12 22 19 14.25 19 9 A 7 7 0 0 0 12 2 z M 9.203125 5.7832031 L 12 5.7832031 L 17.035156 8.3007812 C 17.577776 8.5972613 18.154297 8.7989919 18.154297 9.4199219 L 18.154297 9.9785156 L 16.935547 9.9785156 C 16.700597 10.649795 16.067862 11.097656 15.357422 11.097656 C 14.646982 11.097656 14.013977 10.649795 13.773438 9.9785156 L 10.222656 9.9785156 C 9.9877067 10.649795 9.3549713 11.097656 8.6445312 11.097656 C 7.9340913 11.097656 7.3010869 10.649795 7.0605469 9.9785156 L 5.8476562 9.9785156 L 5.8476562 8.3007812 C 5.8476562 7.6798512 6.3458669 7.1816406 6.9667969 7.1816406 L 7.5253906 7.1816406 L 9.203125 5.7832031 z M 9.484375 6.6230469 L 8.2246094 7.7402344 L 13.958984 7.7402344 L 11.720703 6.6230469 L 9.484375 6.6230469 z M 8.6367188 8.5800781 A 0.8391 0.8391 0 0 0 7.8046875 9.4199219 A 0.8391 0.8391 0 0 0 8.6445312 10.257812 A 0.8391 0.8391 0 0 0 9.484375 9.4199219 A 0.8391 0.8391 0 0 0 8.6445312 8.5800781 A 0.8391 0.8391 0 0 0 8.6367188 8.5800781 z M 15.349609 8.5800781 A 0.8391 0.8391 0 0 0 14.517578 9.4199219 A 0.8391 0.8391 0 0 0 15.357422 10.257812 A 0.8391 0.8391 0 0 0 16.197266 9.4199219 A 0.8391 0.8391 0 0 0 15.357422 8.5800781 A 0.8391 0.8391 0 0 0 15.349609 8.5800781 z ',
      debarcader: 'M 11.46875 1.7226562 A 7 7 0 0 0 4.46875 8.7226562 C 4.46875 13.972656 11.46875 21.722656 11.46875 21.722656 C 11.46875 21.722656 18.46875 13.972656 18.46875 8.7226562 A 7 7 0 0 0 11.46875 1.7226562 z M 10.171875 3.7246094 L 12.960938 3.7246094 L 12.960938 5.1171875 L 14.353516 5.1171875 C 14.864556 5.1171875 15.283203 5.531185 15.283203 6.046875 L 15.283203 8.1933594 L 15.882812 8.3886719 C 16.003603 8.4258319 16.101709 8.5095938 16.162109 8.6210938 C 16.213219 8.7325937 16.226623 8.8623656 16.189453 8.9785156 L 15.306641 12.085938 L 15.283203 12.085938 C 14.539863 12.085938 13.890371 11.67659 13.425781 11.15625 C 12.961201 11.67658 12.309746 12.085938 11.566406 12.085938 C 10.823066 12.085938 10.173574 11.67659 9.7089844 11.15625 C 9.2444041 11.67658 8.5929494 12.085938 7.8496094 12.085938 L 7.8222656 12.085938 L 6.9433594 8.9785156 C 6.9061994 8.8623656 6.9215563 8.7325937 6.9726562 8.6210938 C 7.0330462 8.5095938 7.12921 8.4258419 7.25 8.3886719 L 7.8496094 8.1933594 L 7.8496094 6.046875 A 0.92917 0.92917 0 0 1 8.7792969 5.1171875 L 10.171875 5.1171875 L 10.171875 3.7246094 z M 8.7792969 6.046875 L 8.7792969 7.8867188 L 11.566406 6.9765625 L 14.353516 7.8867188 L 14.353516 6.046875 L 8.7792969 6.046875 z M 9.7089844 12.398438 C 10.842574 13.192877 12.292191 13.192877 13.425781 12.398438 C 13.992571 12.797978 14.637433 13.015625 15.283203 13.015625 L 16.212891 13.015625 L 16.212891 13.945312 L 15.283203 13.945312 C 14.642073 13.945312 14.011161 13.782449 13.425781 13.480469 C 12.264321 14.084429 10.870444 14.084429 9.7089844 13.480469 C 9.1236041 13.782459 8.4860894 13.945312 7.8496094 13.945312 L 6.9199219 13.945312 L 6.9199219 13.015625 L 7.8496094 13.015625 C 8.4953794 13.015625 9.1421941 12.797977 9.7089844 12.398438 z '
    },
    icons: {},
    iconOpacity: 0.6,
    iconScale: 2,
    iconStrokeWeight: 1,

    selectedIconIndex: null,

    fontSize: '14',
    fontColor: '#000000',
    fontWeight: '400',
    labelY: 26,
    labelX: 11,
    zoom: 12,

    styleOptions: [
      'BlueWater',
      'Bright&Bubbly',
      'Cobalt',
      'CoyBeauty',
      'Hopper',
      'IcyBlue',
      'Julyinfo',
      'NeutralBlue',
      'SinCity',
      'THL',
      'TwoTone',
      'WY'
    ],
    selectedStyle: null,
    resize: false
  }),

  watch: {
    tempSettings: _debounce(function (value) {
      if (value) {
        try {
          this.settings = JSON.parse(value)
        } catch (err) {
          this.error = 'Mmmm... Ceva nu e ok, mai incearca.'
        }
      }
    }, 500),
    async selectedStyle (value) {
      try {
        const result = await fetch(`/maps/${this.styleOptions[value]}.json`)
        const json = await result.json()
        this.tempSettings = JSON.stringify(json)
      } catch (err) {
        console.log(err)
      }
    }
  },

  methods: {
    triggerResize () {
      this.resize = !this.resize
    },
    deletePoly (index) {
      this.polys.splice(index, 1)
    },
    addPoly () {
      this.polys.push({
        id: uniqid(),
        label: null,
        visible: true,
        path: null,
        options: {
          fillColor: '#0fb693',
          strokeColor: '#0fb693',
          fillOpacity: 0.6,
          strokeOpacity: 0.6
        }
      })
    },

    openSettings () {
      this.settingsDialog = true
    },
    closeSettings () {
      this.settingsDialog = false
      this.error = null
    },
    alertChanged () {
      this.error = null
    },

    openXlsx () {
      this.xlsxDialog = true
    },
    closeXlsx () {
      this.xlsx = null
      this.$refs.xlsx.$refs.input.value = null
      this.xlsxDialog = false
      // this.append = null
      // this.columnAdresa = 3
      // this.columnCuloare = 0
      // this.columnDenumire = 2
      // this.columnTip = 1
      // this.rowStart = 3
    },
    async parseXlsx () {
      const reader = new FileReader()

      reader.onload = async event => {
        this.closeXlsx()
        this.openMarkers()
        const data = event.target.result
        const workbook = XLSX.read(data, { type: 'binary' })

        const json = XLSX.utils.sheet_to_json(workbook.Sheets[this.sheet], { header: 1, blankrows: false, raw: true, defval: null })

        this.readObjects = json.slice(this.rowStart).map(row => {
          return {
            id: uniqid(),
            culoare: row[this.columnCuloare],
            tip: row[this.columnTip],
            denumire: row[this.columnDenumire],
            adresa: row[this.columnAdresa],
            location: {
              lat: null,
              lng: null
            }
          }
        }).filter(obj => {
          // console.log(obj)
          return obj.denumire && obj.adresa
        })

        this.readObjects.forEach(obj => {
          const pair = {
            label: `${obj.tip} - ${obj.culoare}`,
            color: obj.culoare === 'HCL' ? '#4CAF50' : '#2196F3',
            path: this.getPath(obj.tip)
          }

          this.$set(this.icons, pair.label, pair)
        })

        if (!this.append) {
          this.markers = []
        }

        for (const obj of this.readObjects) {
          await this.geocodeMarker(obj)
          this.markers.push(JSON.parse(JSON.stringify(obj)))
        }
      }

      reader.readAsBinaryString(this.xlsx)
    },

    openMarkers () {
      this.markersDialog = true
    },
    closeMarkers () {
      this.markersDialog = false
    },

    async geocodeMarker (obj) {
      // const api = 'https://maps.googleapis.com/maps/api/geocode/json?address=1600+Amphitheatre+Parkway,+Mountain+View,+CA&key=YOUR_API_KEY'
      const api = 'https://maps.googleapis.com/maps/api/geocode/json?'

      const params = new URLSearchParams()
      params.append('key', 'AIzaSyBXHTApQxC7eBOhAUSi5kiniwnOdztCyKY')
      params.append('address', obj.adresa)

      const url = api + params.toString()

      // should definitely handle errors here
      const response = await fetch(url)
      const result = await response.json()

      obj.location = result.results[0].geometry.location

      return obj
    },

    deleteMarker (index) {
      this.markers.splice(index, 1)
    },

    getPath (tip) {
      switch (tip.trim().toLowerCase()) {
        case 'piata': return this.markersPathsDefault.piata1
        case 'parcare': return this.markersPathsDefault.parcare1
        case 'debarcader': return this.markersPathsDefault.debarcader
        default: return 'M 11.46875 1.7226562 A 7 7 0 0 0 4.46875 8.7226562 C 4.46875 13.972656 11.46875 21.722656 11.46875 21.722656 C 11.46875 21.722656 18.46875 13.972656 18.46875 8.7226562 A 7 7 0 0 0 11.46875 1.7226562 z M 10.171875 3.7246094 L 12.960938 3.7246094 L 12.960938 5.1171875 L 14.353516 5.1171875 C 14.864556 5.1171875 15.283203 5.531185 15.283203 6.046875 L 15.283203 8.1933594 L 15.882812 8.3886719 C 16.003603 8.4258319 16.101709 8.5095938 16.162109 8.6210938 C 16.213219 8.7325937 16.226623 8.8623656 16.189453 8.9785156 L 15.306641 12.085938 L 15.283203 12.085938 C 14.539863 12.085938 13.890371 11.67659 13.425781 11.15625 C 12.961201 11.67658 12.309746 12.085938 11.566406 12.085938 C 10.823066 12.085938 10.173574 11.67659 9.7089844 11.15625 C 9.2444041 11.67658 8.5929494 12.085938 7.8496094 12.085938 L 7.8222656 12.085938 L 6.9433594 8.9785156 C 6.9061994 8.8623656 6.9215563 8.7325937 6.9726562 8.6210938 C 7.0330462 8.5095938 7.12921 8.4258419 7.25 8.3886719 L 7.8496094 8.1933594 L 7.8496094 6.046875 A 0.92917 0.92917 0 0 1 8.7792969 5.1171875 L 10.171875 5.1171875 L 10.171875 3.7246094 z M 8.7792969 6.046875 L 8.7792969 7.8867188 L 11.566406 6.9765625 L 14.353516 7.8867188 L 14.353516 6.046875 L 8.7792969 6.046875 z M 9.7089844 12.398438 C 10.842574 13.192877 12.292191 13.192877 13.425781 12.398438 C 13.992571 12.797978 14.637433 13.015625 15.283203 13.015625 L 16.212891 13.015625 L 16.212891 13.945312 L 15.283203 13.945312 C 14.642073 13.945312 14.011161 13.782449 13.425781 13.480469 C 12.264321 14.084429 10.870444 14.084429 9.7089844 13.480469 C 9.1236041 13.782459 8.4860894 13.945312 7.8496094 13.945312 L 6.9199219 13.945312 L 6.9199219 13.015625 L 7.8496094 13.015625 C 8.4953794 13.015625 9.1421941 12.797977 9.7089844 12.398438 z '
      }
    },

    getSvgImg (pair) {
      return `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" id="Layer_1" x="0px" y="0px" viewBox="0 0 24 24" enable-background="new 0 0 24 24" xml:space="preserve" height="24px" width="24px">
        <g fill="${encodeURIComponent(pair.color)}">
          <path d="${pair.path}" />
        </g>
      </svg>`
    },

    getSvgPolyImg (poly) {
      return `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" id="Layer_1" x="0px" y="0px" viewBox="0 0 512 512" enable-background="new 0 0 24 24" xml:space="preserve" height="24px" width="24px">
        <g fill="${encodeURIComponent(poly.options.labelFillColor)}">
          <path d="${poly.options.markerPath}" />
        </g>
      </svg>`
    },

    exampleSvgImg (path) {
      return `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" id="Layer_1" x="0px" y="0px" viewBox="0 0 24 24" enable-background="new 0 0 24 24" xml:space="preserve" height="24px" width="24px">
        <g fill="${encodeURIComponent(this.icons[this.selectedIconIndex].color)}">
          <path d="${path}" />
        </g>
      </svg>`
    },

    saveData () {
      const data = JSON.stringify({
        tempSettings: this.tempSettings,
        polys: this.polys,
        showBorders: this.showBorders,
        showLabels: this.showLabels,
        markers: this.markers,
        append: this.append,
        columnDenumire: this.columnDenumire,
        columnAdresa: this.columnAdresa,
        columnCuloare: this.columnCuloare,
        columnTip: this.columnTip,
        rowStart: this.rowStart,
        sheet: this.sheet,
        icons: this.icons,
        iconOpacity: this.iconOpacity,
        iconScale: this.iconScale,
        iconStrokeWeight: this.iconStrokeWeight,
        fontColor: this.fontColor,
        fontSize: this.fontSize,
        fontWeight: this.fontWeight,
        labelX: this.labelX,
        labelY: this.labelY,
        zoom: this.zoom
      })

      window.localStorage.setItem('data', data)
    },
    clearData () {
      window.localStorage.removeItem('data')
    },

    screenshot () {
      this.$refs.mapComponent.screenshot()
    },

    updatePolyPath ($event) {
      this.$set(this.polys[$event.index], 'path', $event.str)
    }
  },

  mounted () {
    const data = window.localStorage.getItem('data')

    if (data) {
      const decoded = JSON.parse(data)

      this.tempSettings = decoded.tempSettings
      this.polys = decoded.polys || this.polys
      this.showBorders = decoded.showBorders
      this.showLabels = decoded.showLabels
      this.markers = decoded.markers
      this.append = decoded.append
      this.columnDenumire = decoded.columnDenumire
      this.columnAdresa = decoded.columnAdresa
      this.columnCuloare = decoded.columnCuloare
      this.columnTip = decoded.columnTip
      this.rowStart = decoded.rowStart
      this.sheet = decoded.sheet
      this.icons = decoded.icons
      this.iconOpacity = decoded.iconOpacity
      this.iconScale = decoded.iconScale
      this.iconStrokeWeight = decoded.iconStrokeWeight
      this.fontColor = decoded.fontColor === undefined ? '#000000' : decoded.fontColor
      this.fontSize = decoded.fontSize === undefined ? '14' : decoded.fontSize
      this.fontWeight = decoded.fontWeight === undefined ? '400' : decoded.fontWeight
      this.labelX = decoded.labelX === undefined ? 11 : decoded.labelX
      this.labelY = decoded.labelY === undefined ? 26 : decoded.labelY
      this.zoom = decoded.zoom === undefined ? 13 : decoded.zoom

      this.polys.forEach(poly => {
        if (poly.options.labelScale === undefined) {
          poly.options.labelFillColor = '#ff0000'
          poly.options.labelFillOpacity = 1
          poly.options.labelScale = 0.1
          poly.options.labelStrokeWeight = 1
          poly.options.labelStrokeColor = '#000000'
          poly.options.fontSize = 14
          poly.options.fontWeight = 400
          poly.options.fontColor = '#000000'
          poly.options.markerPath = 'M256,0C153.755,0,70.573,83.182,70.573,185.426c0,126.888,165.939,313.167,173.004,321.035    c6.636,7.391,18.222,7.378,24.846,0c7.065-7.868,173.004-194.147,173.004-321.035C441.425,83.182,358.244,0,256,0z M256,278.719    c-51.442,0-93.292-41.851-93.292-93.293S204.559,92.134,256,92.134s93.291,41.851,93.291,93.293S307.441,278.719,256,278.719z'

          poly.center = null
        }
      })
    }
  }
}
</script>
